import { create } from 'zustand';
import { persist } from 'zustand/middleware';

import { CONTRACT_STATUS_TYPE, CONTRACT_TYPE, DIALOG_MESSAGES } from '../../constants';
import type { TypePrivacyInfo, TypeUserLatestContract } from '../../hooks';
import type {
  TypeCodeState,
  TypeContract,
  TypeSafetyEducation,
  TypeSpecialMedicalExamination,
  TypeUserSubscribe,
} from '../../types';
import { WEB_STORAGE_TYPE, webStorageService } from '~/services';
import { checkOverNDays, isValidDay } from '~/utils';

import { useShouldCheckModalStore } from '../global';

const initialState: TypeUserInfoState = {
  userName: null,
  laborAccountId: -1,
  loginId: '',
  contractType: undefined,
  phoneNumber: '',
  kurlyContract: null,
  privacyInfo: null,
  status: { code: '', text: '' },
  contractAvailableStatus: { code: '', text: '' },
  latestContractSignedAt: null,
  latestContract: null,
  subscribe: null,
  currentContractStatus: { code: '', text: '' },
  certificationStatus: { code: '', text: '' },
  safetyEducation: null,
  initPassword: false,
  passwordChangeRequired: false,
  specialMedicalExamination: null,
  reConsentRequired: false,
};

const keyOfState = Object.keys(initialState) as (keyof TypeUserInfoState)[];

export type TypeUserInfoState = {
  userName: string | null;
  loginId: string;
  laborAccountId: number;
  contractType: TypeContract | undefined;
  phoneNumber: string;
  kurlyContract: {
    cluster: TypeCodeState;
    center: TypeCodeState;
    workPart: TypeCodeState;
    contractedDate?: string;
    contractedAt?: string;
  } | null;
  privacyInfo: TypePrivacyInfo | null;
  status: TypeCodeState;
  contractAvailableStatus: TypeCodeState | null;
  latestContractSignedAt: string | null;
  latestContract: TypeUserLatestContract | null;
  subscribe: TypeUserSubscribe | null;
  currentContractStatus: TypeCodeState | null;
  certificationStatus: TypeCodeState | null;
  safetyEducation: TypeSafetyEducation | null;
  initPassword: boolean;
  passwordChangeRequired: boolean;
  specialMedicalExamination: TypeSpecialMedicalExamination | null;
  reConsentRequired: boolean;
};

type TypeUserInfoStore = TypeUserInfoState & {
  actions: {
    setUserInfo: (userInfo: Partial<TypeUserInfoState>) => void;
    resetUserInfo: () => void;
    /** 개인정보가 입력되었는지 여부를 반환 */
    getHasPrivacyInfo: () => boolean;
    /** 계약 유형이 아르바이트(단기)인지 여부를 반환 */
    getIsContractTypeShort: () => boolean;
    /** 안전교육 대상 여부를 반환 */
    getIsNeedSafetyTraining: () => boolean;
    /** 비밀번호 변경 대상 여부를 반환 */
    getIsPasswordChangeTarget: () => { open: boolean; type: 'initPassword' | 'over90Days'; message?: string };
  };
};

/** @description 상태를 로컬 스토리지에 저장하기 위한 키 */
const keyOfStorage = 'userInfo';

/** @description 유저 정보를 관리하는 상태 */
export const useUserInfoStore = create<TypeUserInfoStore>()(
  persist(
    (set, get) => ({
      ...initialState,
      actions: {
        setUserInfo: (userInfo: Partial<TypeUserInfoState>) => set((state) => ({ ...state, ...userInfo })),
        resetUserInfo: () => {
          set({ ...initialState });
          localStorage.removeItem(keyOfStorage);
        },
        getIsContractTypeShort: () => get().contractType === CONTRACT_TYPE.SHORT,
        getHasPrivacyInfo: () => {
          const privacyInfo = get().privacyInfo;
          return (
            privacyInfo !== null &&
            privacyInfo.juminNo !== null &&
            privacyInfo.address !== null &&
            privacyInfo.financialInstitution !== null &&
            privacyInfo.accountNo !== null
          );
        },
        getIsNeedSafetyTraining: () =>
          get().contractType === CONTRACT_TYPE.SHORT && get().safetyEducation?.status?.code !== 'COMPLETED',
        getIsPasswordChangeTarget: () => {
          // 비밀번호 변경 모달은 아래 조건을 모두 만족해야 표시됨
          // 1. 비밀번호 변경 대상자인 경우
          // 2. 다음에 변경하기 상태를 지정한 이후 30일이 지났거나 지정하지 않은 경우
          // 3. 앱 실행 후 최초 모달이 노출되는 경우
          // 4. 약관 동의 대상이 아닌 경우
          const webStorage = webStorageService(WEB_STORAGE_TYPE.LOCAL_STORAGE);
          const skipDate = webStorage.getSkipChangePasswordDate();

          const isInitPassword = get().initPassword;
          const isPasswordChangeRequired = get().passwordChangeRequired;
          const isAlreadyChecked = useShouldCheckModalStore.getState().changePasswordModal.isChecked;

          const isReConsentRequired = get().reConsentRequired;
          const isTarget = isInitPassword || isPasswordChangeRequired;
          const isOver30DaysOrEmpty = !isValidDay(skipDate) || checkOverNDays(skipDate, 30);

          return {
            open: isTarget && isOver30DaysOrEmpty && !isAlreadyChecked && !isReConsentRequired,
            type: isInitPassword ? 'initPassword' : 'over90Days',
            message: isInitPassword ? DIALOG_MESSAGES.SHOULD_CHANGE_PASSWORD : DIALOG_MESSAGES.NEED_TO_CHANGE_PASSWORD,
          };
        },
      },
    }),
    {
      name: keyOfStorage,
      version: 1,
      partialize: (state) =>
        keyOfState.reduce(
          (acc, key) => ({
            ...acc,
            [key]: state[key],
          }),
          {}
        ),
      migrate: (persistedState, currentVersion) => {
        // UI/UX 개선 전 로컬스토리지에 저장된 값 삭제
        if (currentVersion < 1) {
          localStorage.removeItem('user');
        }
        return persistedState;
      },
    }
  )
);
